import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowClockwise, ArrowCounterClockwise, ClipboardText, Export, FileCsv, FileXls, Plus, WarningCircle,
} from '@phosphor-icons/react';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import { useNotification } from '../../../../contexts/NotificationContext';
import { AnsichtUmschalter, BestaetigenDialog, FlipCard, ImmoButton, ListenSuche, MenueEintrag, ObjektFilter, Popover, SegmentedControl, Seitennavigation, ToastPill, useToastPill } from '../../../../components/immobilien/ui';
import { seiteAus } from '../../../../lib/immobilien/seiten';
import { heuteIso } from '../../../../lib/immobilien/format';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import {
  basisZeilen, baueMieterZeilen, FILTER_LABEL, FILTER_REIHENFOLGE, istListenFilter, kennzahlen, naechsteWechsel, objektOptionen,
  passtFilter, zaehleFilter, type ListenFilter,
} from '../../../../lib/mieter/liste';
import { useMieterListe } from '../../../../lib/mieter/useMieterListe';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import type { ExportColumn } from '../../../../lib/export/types';
import MieterCsvImportFormular from '../MieterCsvImportFormular';
import { NeuerMieterFormular } from '../neu/NeuerMieterFormular';
import { MieterWidgets } from './MieterWidgets';
import { MieterKachel } from './MieterKachel';
import { MieterTabelle } from './MieterTabelle';
import PortalSchalterKarte from '../../../../components/portal/PortalSchalterKarte';

const geld = (v: unknown) => (v != null && v !== '' ? `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '');

/** Spalten des Mieter-Exports – unverändert aus der bisherigen Liste. */
const MIETER_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'name', label: 'Name', format: (_, row) => `${row.vorname || ''} ${row.nachname || ''}`.trim() },
  { key: 'email', label: 'E-Mail' },
  { key: 'telefon', label: 'Telefon' },
  { key: 'status', label: 'Status' },
  { key: 'einheit', label: 'Einheit' },
  { key: 'kaltmiete', label: 'Kaltmiete', format: geld },
  { key: 'nebenkosten_vorauszahlung', label: 'Nebenkosten', format: geld },
  { key: 'kaution', label: 'Kaution', format: geld },
];

type Ansicht = 'kacheln' | 'liste';
/** Mieter je Seite (Wunsch 13.10.2026) */
const PRO_SEITE = 20;

/** Kopf-Knopf (Prototyp): 40 px, --fill2 mit Linie, 13/600. */
const KOPF_STIL: React.CSSProperties = { boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 };

export interface MieterListeSeiteProps {
  initialMieterId?: string | null;
  onClearInitial?: () => void;
}

export default function MieterListeSeite({ initialMieterId, onClearInitial }: MieterListeSeiteProps) {
  const navigate = useNavigate();
  const { openForm, closeForm } = useIslandActions();
  const { showToast } = useNotification();
  const [params, setParams] = useSearchParams();
  const heute = heuteIso();
  const { zustand, neuLaden } = useMieterListe(heute);
  const [suche, setSuche] = useState('');
  const [exportiert, setExportiert] = useState(false);
  const [formularGeaendert, setFormularGeaendert] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);
  const [neuId, setNeuId] = useState<string | null>(null);
  const [toast, zeige] = useToastPill(4200);

  const filter: ListenFilter = istListenFilter(params.get('f')) ? params.get('f') as ListenFilter : 'aktiv';
  const objekt = params.get('obj') ?? 'alle';
  const ansicht: Ansicht = params.get('view') === 'liste' ? 'liste' : 'kacheln';
  const formularOffen = params.get('neu') === '1';

  const setzeParam = useCallback((schluessel: string, wert: string, standard: string) => {
    setParams(p => {
      const neu = new URLSearchParams(p);
      if (wert === standard) neu.delete(schluessel); else neu.set(schluessel, wert);
      // Anderer Filter, anderes Objekt: wieder auf Seite 1
      if (schluessel !== 'seite') neu.delete('seite');
      return neu;
    });
  }, [setParams]);
  // Suche (lokal): ebenfalls zurück auf Seite 1
  const sucheAendern = (v: string) => {
    setSuche(v);
    if (params.get('seite')) setParams(p => { const n = new URLSearchParams(p); n.delete('seite'); return n; }, { replace: true });
  };

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const mietkontoBereit = daten?.mietkonto.status === 'ok';

  const zeilen = useMemo(() => (daten ? baueMieterZeilen({
    mieter: daten.mieter,
    objekte: daten.objekte,
    einheiten: daten.einheiten,
    portal: daten.portal,
    konten: daten.mietkonto.status === 'ok' ? daten.mietkonto.konten : null,
    heute,
  }) : []), [daten, heute]);

  const k = useMemo(() => kennzahlen(zeilen, mietkontoBereit), [zeilen, mietkontoBereit]);
  const wechsel = useMemo(() => naechsteWechsel(zeilen, heute), [zeilen, heute]);
  const optionen = useMemo(() => (daten ? objektOptionen(daten.objekte, zeilen) : []), [daten, zeilen]);
  const basis = useMemo(() => basisZeilen(zeilen, objekt, suche), [zeilen, objekt, suche]);
  const zaehler = useMemo(() => zaehleFilter(basis), [basis]);
  // Neu angelegter Mieter steht vorne (grüner Ring)
  const sichtbar = useMemo(() => {
    const liste = basis.filter(z => passtFilter(z, filter));
    const i = neuId ? liste.findIndex(z => z.id === neuId) : -1;
    return i > 0 ? [liste[i]!, ...liste.slice(0, i), ...liste.slice(i + 1)] : liste;
  }, [basis, filter, neuId]);
  const alleAnzahl = useMemo(() => zeilen.filter(z => !z.inaktiv).length, [zeilen]);

  // 20 Mieter je Seite; die Seite steht in der URL (?seite=), Zurück-Taste funktioniert
  const listeOben = useRef<HTMLDivElement>(null);
  const seiteRoh = Number(params.get('seite') ?? '1');
  const seite = useMemo(() => seiteAus(sichtbar, seiteRoh, PRO_SEITE), [sichtbar, seiteRoh]);
  const gezeigt = seite.eintraege;
  const zuSeite = useCallback((n: number) => {
    setzeParam('seite', String(n), '1');
    listeOben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setzeParam]);

  const oeffne = useCallback((id: string) => {
    mitViewTransition(() => {
      navigate(`/admin/mieter/${encodeURIComponent(id)}`);
      window.scrollTo(0, 0);
    });
  }, [navigate]);

  // Einstieg von außen (initialMieterId): Mieter direkt öffnen
  useEffect(() => {
    if (!initialMieterId) return;
    navigate(`/admin/mieter/${encodeURIComponent(initialMieterId)}`);
    onClearInitial?.();
  }, [initialMieterId, navigate, onClearInitial]);

  const schliesseFormular = useCallback(() => {
    setVerwerfen(false);
    setFormularGeaendert(false);
    setParams(p => { const n = new URLSearchParams(p); n.delete('neu'); return n; });
  }, [setParams]);
  const umschalten = () => {
    if (!formularOffen) { setParams(p => { const n = new URLSearchParams(p); n.set('neu', '1'); return n; }); return; }
    if (formularGeaendert) setVerwerfen(true); else schliesseFormular();
  };
  const gespeichert = useCallback((id: string, meldung: string) => {
    setNeuId(id);
    setFormularGeaendert(false);
    setSuche('');
    // zurückdrehen und den neuen Mieter in der Liste zeigen (Filter „Aktiv“, alle Objekte)
    setParams(p => { const n = new URLSearchParams(p); n.delete('neu'); n.delete('f'); n.delete('obj'); n.delete('seite'); return n; });
    neuLaden();
    zeige(meldung);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setParams, neuLaden, zeige]);
  const csvImport = () => openForm(<MieterCsvImportFormular onDone={() => { closeForm(); neuLaden(); }} />, 'CSV Import', 860,
    { icon: FileCsv, farbe: 'gruen', badge: 'hochladen' });

  const exportiere = (format: 'csv' | 'xlsx') => {
    if (!daten) return;
    setExportiert(true);
    try {
      const config = {
        title: 'Mieter',
        columns: MIETER_EXPORT_COLUMNS,
        data: daten.mieter as Record<string, unknown>[],
        stats: { items: [
          { label: 'Gesamt', value: daten.mieter.length },
          { label: 'Aktiv', value: daten.mieter.filter(m => m.status === 'aktiv').length },
          { label: 'Inaktiv', value: daten.mieter.filter(m => m.status !== 'aktiv').length },
        ] },
      };
      if (format === 'csv') exportCsv(config); else exportXlsx(config);
    } catch (e) {
      showToast({ type: 'error', message: `Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}` });
    } finally {
      setExportiert(false);
    }
  };

  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Mieter</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>
            {daten ? `${k.aktiv} aktive Mietverhältnisse · ${k.objekte} Objekte` : 'Wird geladen …'}
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <Popover
            label="Export"
            breite={200}
            ausloeser={({ offen, umschalten, ref }) => (
              <ImmoButton ref={ref} groesse={40} icon={Export} aria-haspopup="menu" aria-expanded={offen} onClick={umschalten} disabled={!daten || exportiert} style={KOPF_STIL}>
                {exportiert ? 'Exportiert …' : 'Export'}
              </ImmoButton>
            )}
          >
            {schliessen => (
              <>
                <MenueEintrag onClick={() => { schliessen(); exportiere('csv'); }}><FileCsv size={16} color="var(--ok)" />CSV</MenueEintrag>
                <MenueEintrag onClick={() => { schliessen(); exportiere('xlsx'); }}><FileXls size={16} color="var(--info)" />Excel (XLSX)</MenueEintrag>
              </>
            )}
          </Popover>
          <ImmoButton groesse={40} icon={FileCsv} onClick={csvImport} style={KOPF_STIL}>CSV-Import</ImmoButton>
          <ImmoButton groesse={40} icon={ClipboardText} onClick={() => navigate('/admin/mieterselbstauskunft')} style={KOPF_STIL}>Selbstauskunft</ImmoButton>
          <ImmoButton art={formularOffen ? 'fuellung' : 'akzent'} groesse={40} icon={Plus} iconGedreht={formularOffen} aria-expanded={formularOffen}
            onClick={umschalten} style={{ padding: '0 16px', ...(formularOffen ? { fontWeight: 800 } : {}) }}>
            {formularOffen ? 'Abbrechen' : 'Neuer Mieter'}
          </ImmoButton>
        </div>
      </header>

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Mieter konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}

      {daten && (
        <FlipCard
          gedreht={formularOffen}
          vorne={(
            <MieterWidgets
              k={k}
              wechsel={wechsel}
              onAktiv={() => setzeParam('f', 'aktiv', 'aktiv')}
              onRueckstand={() => setzeParam('f', 'rueck', 'aktiv')}
              onMieter={oeffne}
            />
          )}
          hinten={formularOffen ? (
            <NeuerMieterFormular daten={daten} objekte={optionen} heute={heute} onGeaendert={setFormularGeaendert} onGespeichert={gespeichert} />
          ) : null}
        />
      )}
      {verwerfen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die Eingaben für den neuen Mieter gehen verloren."
          bestaetigenLabel="Verwerfen" icon={ArrowCounterClockwise} onBestaetigen={schliesseFormular} onAbbrechen={() => setVerwerfen(false)} />
      )}
      {daten?.mietkonto.status === 'fehler' && (
        <span role="status" style={{ fontSize: 12, color: 'var(--bad)' }}>Mietkonto konnte nicht geladen werden: {daten.mietkonto.meldung}</span>
      )}

      <PortalSchalterKarte art="mieterportal" onMeldung={zeige} />

      <div ref={listeOben} className="flex flex-wrap items-center" style={{ gap: 10, scrollMarginTop: 16 }}>
        <ListenSuche wert={suche} onChange={sucheAendern} platzhalter="Name, E-Mail, Einheit …" ariaLabel="Mieter durchsuchen" />
        <SegmentedControl
          art="akzent"
          ariaLabel="Status"
          wert={filter}
          onChange={f => setzeParam('f', f, 'aktiv')}
          segmente={FILTER_REIHENFOLGE.map(f => ({ key: f, label: `${FILTER_LABEL[f]} · ${zaehler[f]}` }))}
        />
        <ObjektFilter optionen={optionen} wert={objekt} onChange={id => setzeParam('obj', id, 'alle')} alleAnzahl={alleAnzahl} />
        <AnsichtUmschalter wert={ansicht} onChange={a => setzeParam('view', a, 'kacheln')} />
      </div>

      {zustand.status === 'laedt' && (
        <section aria-busy="true" aria-label="Mieter werden geladen" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 176, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </section>
      )}

      {daten && sichtbar.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: 34, gap: 10, fontSize: 14, color: 'var(--ink3)' }}>
          {zeilen.length === 0 ? 'Noch keine Mieter angelegt' : 'Keine Mieter unter diesen Kriterien gefunden'}
          {(suche || objekt !== 'alle' || filter !== 'aktiv') && zeilen.length > 0 && (
            <ImmoButton onClick={() => { setSuche(''); setParams(p => { const n = new URLSearchParams(p); n.delete('f'); n.delete('obj'); n.delete('seite'); return n; }); }}>Filter zurücksetzen</ImmoButton>
          )}
        </div>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'kacheln' && (
        <section aria-label="Mieter" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
          {gezeigt.map(z => <MieterKachel key={z.id} z={z} neu={z.id === neuId} onOeffnen={oeffne} />)}
        </section>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'liste' && <MieterTabelle zeilen={gezeigt} onOeffnen={oeffne} />}
      {daten && sichtbar.length > 0 && (
        <Seitennavigation seite={seite.seite} seiten={seite.seiten} von={seite.von} bis={seite.bis} gesamt={seite.gesamt} einheit="Mieter" onSeite={zuSeite} />
      )}
    </>
  );
}
